import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function FollowupTab({
  autoFollowupEnabled, toggleFollowup, togglingFollowup,
  followupSteps, updateStep, saveFollowupConfig, savingConfig,
  followupStats, triggerFollowupNow, triggeringFollowup
}) {
  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
        <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-400" />
          Relances Automatiques
        </h2>
        <p className="text-[#A3A3A3] text-sm mb-6">
          Personnalisez les délais et objets d'email pour chaque relance.
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between bg-[#0A0A0A] rounded-xl p-4 border border-[#262626]">
            <div>
              <p className="text-white font-medium">Activer les relances</p>
              <p className="text-[#666] text-xs mt-1">Vérifie toutes les heures les leads en attente</p>
            </div>
            <button
              onClick={toggleFollowup}
              disabled={togglingFollowup}
              className={`relative w-14 h-7 rounded-full transition-colors ${autoFollowupEnabled ? 'bg-blue-500' : 'bg-[#333]'}`}
              data-testid="followup-toggle-switch"
            >
              <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${autoFollowupEnabled ? 'translate-x-8' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="space-y-3">
            {followupSteps.map((step, i) => {
              const colors = ['blue', '#EFFF00', '#ff4444'];
              const labels = ['Rappel doux', 'Urgence offre', 'Dernière chance'];
              const color = colors[i] || 'blue';
              return (
                <div key={step.key} className={`bg-[#0A0A0A] rounded-xl p-4 border ${step.enabled ? 'border-[#262626]' : 'border-[#1a1a1a] opacity-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold uppercase tracking-wider" style={{color}}>{labels[i] || `Relance ${i+1}`}</span>
                      <span className="text-[#666] text-xs">({step.key})</span>
                    </div>
                    <button
                      onClick={() => updateStep(i, 'enabled', !step.enabled)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${step.enabled ? 'bg-green-500' : 'bg-[#333]'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${step.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="grid grid-cols-[120px_1fr] gap-3">
                    <div>
                      <label className="block text-[#666] text-xs mb-1">Délai (heures)</label>
                      <input
                        type="number" value={step.hours}
                        onChange={(e) => updateStep(i, 'hours', parseInt(e.target.value) || 1)}
                        className="w-full bg-[#1a1a1a] border border-[#262626] rounded-lg px-3 py-2 text-white text-sm" min="1"
                        data-testid={`followup-hours-${i}`}
                      />
                      <p className="text-[#444] text-[10px] mt-1">
                        = {step.hours < 24 ? `${step.hours}h` : `${Math.round(step.hours / 24)}j`}
                      </p>
                    </div>
                    <div>
                      <label className="block text-[#666] text-xs mb-1">Objet de l'email</label>
                      <input
                        type="text" value={step.subject}
                        onChange={(e) => updateStep(i, 'subject', e.target.value)}
                        className="w-full bg-[#1a1a1a] border border-[#262626] rounded-lg px-3 py-2 text-white text-sm"
                        data-testid={`followup-subject-${i}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <Button onClick={saveFollowupConfig} disabled={savingConfig} className="w-full bg-[#EFFF00] text-black hover:bg-[#d4e000]" data-testid="save-followup-config">
            {savingConfig ? "Sauvegarde..." : "Sauvegarder la configuration"}
          </Button>

          {followupStats && (
            <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#262626]">
              <p className="text-[#A3A3A3] text-xs font-bold uppercase tracking-wider mb-3">Statistiques</p>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <p className="text-white font-bold text-xl">{followupStats.pending_leads}</p>
                  <p className="text-[#666] text-xs">En attente</p>
                </div>
                <div>
                  <p className="text-blue-400 font-bold text-xl">{followupStats.relance_1_sent}</p>
                  <p className="text-[#666] text-xs">Relance 1</p>
                </div>
                <div>
                  <p className="text-[#EFFF00] font-bold text-xl">{followupStats.relance_2_sent}</p>
                  <p className="text-[#666] text-xs">Relance 2</p>
                </div>
                <div>
                  <p className="text-red-400 font-bold text-xl">{followupStats.relance_3_sent}</p>
                  <p className="text-[#666] text-xs">Relance 3</p>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={triggerFollowupNow}
            disabled={triggeringFollowup || !autoFollowupEnabled}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            data-testid="trigger-followup-btn"
          >
            {triggeringFollowup ? (
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Envoi en cours...
              </span>
            ) : (
              <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Lancer les relances maintenant</span>
            )}
          </Button>
          {!autoFollowupEnabled && (
            <p className="text-[#666] text-xs text-center">Activez les relances pour pouvoir les déclencher</p>
          )}
        </div>
      </div>
    </div>
  );
}
